import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Save, Rocket, Target, DollarSign, Users, TrendingDown, Clock, TrendingUp, Gauge } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const METRIC_DEFINITIONS = [
  { key: 'mrr', name: 'Monthly Recurring Revenue', icon: DollarSign, unit: '$', color: 'cyan', higherBetter: true, desc: 'Target MRR for healthy startups' },
  { key: 'cac', name: 'Customer Acquisition Cost', icon: Target, unit: '$', color: 'amber', higherBetter: false, desc: 'Maximum acceptable CAC' },
  { key: 'churn', name: 'Churn Rate', icon: TrendingDown, unit: '%', color: 'red', higherBetter: false, desc: 'Maximum acceptable monthly churn' },
  { key: 'ltv', name: 'Lifetime Value', icon: Users, unit: '$', color: 'green', higherBetter: true, desc: 'Target customer LTV' },
  { key: 'burn_rate', name: 'Burn Rate', icon: TrendingDown, unit: '$', color: 'orange', higherBetter: false, desc: 'Maximum monthly burn rate' },
  { key: 'runway', name: 'Cash Runway', icon: Clock, unit: 'months', color: 'blue', higherBetter: true, desc: 'Minimum months of runway' },
  { key: 'growth', name: 'Customer Growth Rate', icon: TrendingUp, unit: '%', color: 'emerald', higherBetter: true, desc: 'Target monthly growth rate' },
  { key: 'pmf', name: 'Product-Market Fit Score', icon: Gauge, unit: '%', color: 'purple', higherBetter: true, desc: 'Target PMF score (very disappointed %)' },
];

const AdminBenchmarks = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [benchmarks, setBenchmarks] = useState(
    METRIC_DEFINITIONS.map(m => ({
      metric_key: m.key,
      metric_name: m.name,
      benchmark_value: 0,
      points_weight: 12,
      description: m.desc,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBenchmarks();
  }, []);

  const loadBenchmarks = async () => {
    try {
      const res = await axios.get(`${API}/admin/benchmarks`, { withCredentials: true });
      if (res.data.length > 0) {
        const existing = {};
        res.data.forEach(b => { existing[b.metric_key] = b; });
        setBenchmarks(prev =>
          prev.map(b => existing[b.metric_key]
            ? { ...b, benchmark_value: existing[b.metric_key].benchmark_value, points_weight: existing[b.metric_key].points_weight }
            : b
          )
        );
      }
    } catch {
      toast.error('Failed to load benchmarks');
    } finally {
      setLoading(false);
    }
  };

  const updateBenchmark = (key, field, value) => {
    setBenchmarks(prev =>
      prev.map(b => b.metric_key === key ? { ...b, [field]: parseFloat(value) || 0 } : b)
    );
  };

  const totalPoints = benchmarks.reduce((sum, b) => sum + b.points_weight, 0);

  const handleSave = async () => {
    if (totalPoints !== 100) {
      toast.error(`Total points must equal 100. Current: ${totalPoints}`);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/benchmarks`, benchmarks, { withCredentials: true });
      toast.success('Benchmarks saved successfully');
    } catch {
      toast.error('Failed to save benchmarks');
    } finally {
      setSaving(false);
    }
  };

  const colorMap = {
    cyan: 'from-cyan-500/20 to-cyan-600/20 text-cyan-400',
    amber: 'from-amber-500/20 to-amber-600/20 text-amber-400',
    red: 'from-red-500/20 to-red-600/20 text-red-400',
    green: 'from-green-500/20 to-green-600/20 text-green-400',
    orange: 'from-orange-500/20 to-orange-600/20 text-orange-400',
    blue: 'from-blue-500/20 to-blue-600/20 text-blue-400',
    emerald: 'from-emerald-500/20 to-emerald-600/20 text-emerald-400',
    purple: 'from-purple-500/20 to-purple-600/20 text-purple-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="admin-benchmarks">
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                StartupBlackbox
              </span>
            </div>
            <span className="px-3 py-1 bg-red-500/10 text-red-400 text-sm rounded-full border border-red-500/30">
              Admin Panel
            </span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-slate-400 hover:text-slate-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-100">Metric Benchmarks</h1>
            <p className="text-slate-400 mt-1">Set target benchmarks and point weights for startup health scores</p>
          </div>
          <div className="flex items-center gap-4">
            <div className={`px-4 py-2 rounded-lg text-sm font-medium ${totalPoints === 100 ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
              Total Points: {totalPoints}/100
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || totalPoints !== 100}
              data-testid="save-benchmarks-btn"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Benchmarks'}
            </Button>
          </div>
        </div>

        <div className="grid gap-4">
          {benchmarks.map((b) => {
            const def = METRIC_DEFINITIONS.find(d => d.key === b.metric_key);
            const Icon = def.icon;
            return (
              <Card key={b.metric_key} className="bg-slate-800/40 border-slate-700 p-5" data-testid={`benchmark-${b.metric_key}`}>
                <div className="flex items-center gap-6">
                  <div className={`w-12 h-12 bg-gradient-to-br ${colorMap[def.color]} rounded-lg flex items-center justify-center shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-100">{b.metric_name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${def.higherBetter ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                        {def.higherBetter ? 'Higher is better' : 'Lower is better'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{b.description}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Target ({def.unit})</label>
                      <Input
                        type="number"
                        value={b.benchmark_value || ''}
                        onChange={(e) => updateBenchmark(b.metric_key, 'benchmark_value', e.target.value)}
                        className="w-32 bg-slate-700/50 border-slate-600 text-slate-100"
                        data-testid={`benchmark-value-${b.metric_key}`}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Points</label>
                      <Input
                        type="number"
                        value={b.points_weight || ''}
                        onChange={(e) => updateBenchmark(b.metric_key, 'points_weight', e.target.value)}
                        className="w-20 bg-slate-700/50 border-slate-600 text-slate-100"
                        data-testid={`benchmark-points-${b.metric_key}`}
                        min={0}
                        max={100}
                      />
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminBenchmarks;
